import { MessageType } from 'ts-components/Chat';
import {
  ChatMessageFromAPI,
  ChatMessageData,
  ChatMessageDataStreaming,
} from './types';

const HIDDEN_OUTPUT_VARIABLES = ['alert', 'question', 'answer'];

export const getMessageContent = (
  data: ChatMessageData,
  name: keyof ChatMessageData
) => {
  return Array.isArray(data[name as keyof ChatMessageData])
    ? (data[name as keyof ChatMessageData] as string[]).join(' ')
    : (data[name as keyof ChatMessageData] as string);
};
export const chatMessageMapper = (messages: ChatMessageFromAPI[]) => {
  return messages.reduce(
    (
      acc: {
        messages: MessageType[];
        answers: string[];
        alert: string;
        numberOfNewMessages: number;
      },
      message: ChatMessageFromAPI
    ) => {
      const { id, role, data, raw_content } = message;
      const isUserMessage = role === 'user';

      // Filter out unneeded fields with 'json' in their keys
      const filteredData = Object.keys(data)
        .filter((key) => !key.includes('json'))
        .reduce(
          (obj: Record<string, string | string[]>, key: string) => {
            obj[key as keyof ChatMessageData] =
              data[key as keyof ChatMessageData];
            return obj;
          },
          {} as Record<string, string | string[]>
        );

      const dataExists = Object.keys(filteredData).some(
        (key) =>
          !Array.isArray(filteredData[key]) || filteredData[key].length > 0
      );

      if (dataExists && typeof filteredData?.alert === 'string') {
        acc.alert = filteredData?.alert;
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          isCollapsable: false,
          messageContent: filteredData?.alert,
        });
      } else if (dataExists && isUserMessage && filteredData?.text) {
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          isCollapsable: false,
          messageContent: data?.text,
        });
      } else if (
        dataExists &&
        typeof filteredData?.question === 'string' &&
        Array.isArray(filteredData?.answer)
      ) {
        acc.answers = filteredData?.answer;
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          isCollapsable: false,
          messageContent: filteredData?.question,
        });
      } else if (dataExists) {
        let outputVars = Object.keys(filteredData);
        acc.answers = [];
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        // ensure user_output is always the first message and populate and other varaibles
        if (
          filteredData?.user_output &&
          filteredData?.user_output?.length > 0
        ) {
          outputVars = [
            'user_output',
            ...Object.keys(filteredData).filter(
              (name) =>
                name !== 'user_output' &&
                !HIDDEN_OUTPUT_VARIABLES.includes(name) &&
                filteredData[name]?.length !== 0
            ),
          ];
        } else {
          outputVars = Object.keys(filteredData).filter(
            (name) =>
              !HIDDEN_OUTPUT_VARIABLES.includes(name) &&
              filteredData[name]?.length !== 0
          );
        }
        // since we are pushing more than one message, return number of new messages
        acc.numberOfNewMessages = outputVars.length;
        outputVars.forEach((name) => {
          if (name === 'user_output') {
            acc.messages.push({
              id: `${id}-user_output`,
              isUserMessage,
              isResponseLoading: false,
              isCollapsable: false,
              messageContent: String(filteredData?.user_output),
            });
            return;
          }
          acc.messages.push({
            id: `${id}-${name}`,
            isUserMessage,
            isResponseLoading: false,
            isCollapsable: true,
            header: name
              .split('_')
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
              )
              .join(' '),
            messageContent: getMessageContent(
              filteredData as ChatMessageData,
              name as keyof ChatMessageData
            ),
          });
        });
      } else {
        acc.answers = [];
        acc.alert = '';
        acc.numberOfNewMessages = 1;
        acc.messages.push({
          id,
          isUserMessage,
          isResponseLoading: false,
          isCollapsable: false,
          messageContent: raw_content,
        });
      }
      return acc;
    },
    { messages: [], answers: [], numberOfNewMessages: 1, alert: '' }
  );
};

export const getAnswersOrAlertsByMessage = (
  data: ChatMessageDataStreaming
): {
  messages: MessageType[];
  answers: string[];
  alert: string;
  numberOfNewMessages: number;
} => {
  const dataExists = Object.keys(data).some((key) => {
    const value = data[key as keyof ChatMessageDataStreaming];
    return !Array.isArray(value) || value.length > 0;
  });
  const acc = {
    messages: [],
    answers: [],
    alert: '',
    numberOfNewMessages: 1,
  } as {
    messages: MessageType[];
    answers: string[];
    alert: string;
    numberOfNewMessages: number;
  };

  if (dataExists && typeof data?.alert === 'string') {
    acc.answers = [];
    acc.alert = data?.alert;
  } else if (
    dataExists &&
    typeof data?.question === 'string' &&
    Array.isArray(data?.answer)
  ) {
    acc.answers = data?.answer;
    acc.alert = '';
  } else {
    acc.answers = [];
    acc.alert = '';
  }
  return acc;
};
